/**
 * rev 3.3 §6.4/§6.7 — the Agent's takeover DEFAULTS and operator chat as the
 * Edit form sees them. Pure (no React) so the load/save mapping is unit-tested.
 */
import type { MyBridgeAgent, MyBridgeAgentInput, MyBridgeTakeoverDefaults } from '@common/reducers/backend-api/backend-api';

/**
 * The defaults an agent MEANS. The bridge always returns `takeover` (a row that
 * predates it is derived server-side, lib-bridge `deriveAgentTakeoverDefaults`);
 * a new agent (no row) or a missing field is `true`, what the bridge creates by default.
 */
export function agentTakeoverDefaults(agent: Pick<MyBridgeAgent, 'takeover'> | null | undefined): MyBridgeTakeoverDefaults {
  const stored = agent?.takeover;
  return {
    enabled: typeof stored?.enabled === 'boolean' ? stored.enabled : true,
    byIncomingMessage: typeof stored?.byIncomingMessage === 'boolean' ? stored.byIncomingMessage : true,
  };
}

/** The slice of the form this module maps. */
export interface TakeoverFormFields {
  takeoverEnabled: boolean;
  takeoverByIncoming: boolean;
  /** '' = no operator chat. */
  operatorChatId: string;
  /** #1059 — resume by itself after an automatic takeover. */
  autoResumeEnabled: boolean;
  /** Minutes as typed ('' while empty); only meaningful when `autoResumeEnabled`. */
  autoResumeMinutes: string;
}

/** Offered when the owner switches self-resume on without typing a number. */
export const AUTO_RESUME_DEFAULT_MINUTES = 30;
export const AUTO_RESUME_MIN = 1;
export const AUTO_RESUME_MAX = 1440;

/** The minutes the text means, or null when it is not a whole number in [1, 1440]. */
export function parseAutoResumeMinutes(text: string): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= AUTO_RESUME_MIN && n <= AUTO_RESUME_MAX ? n : null;
}

/** True when self-resume is on but the minutes are not a valid whole number - the form must not save. */
export function autoResumeInvalid(f: Pick<TakeoverFormFields, 'autoResumeEnabled' | 'autoResumeMinutes'>): boolean {
  return f.autoResumeEnabled && parseAutoResumeMinutes(f.autoResumeMinutes) === null;
}

export function takeoverFormFrom(
  agent: Pick<MyBridgeAgent, 'takeover' | 'operatorChatId' | 'autoResumeAfterMinutes'> | null | undefined,
): TakeoverFormFields {
  const d = agentTakeoverDefaults(agent);
  const stored = agent?.autoResumeAfterMinutes;
  const minutes = typeof stored === 'number' && Number.isInteger(stored) && stored >= AUTO_RESUME_MIN && stored <= AUTO_RESUME_MAX ? stored : null;
  return {
    takeoverEnabled: d.enabled,
    takeoverByIncoming: d.byIncomingMessage,
    operatorChatId: agent?.operatorChatId ?? '',
    autoResumeEnabled: minutes !== null,
    autoResumeMinutes: minutes !== null ? String(minutes) : '',
  };
}

/**
 * The save payload for these fields: ONLY what changed against what was loaded
 * (`loaded`). An untouched form sends nothing, so saving an unrelated field never
 * rewrites the takeover defaults / operator chat (and never trips a server that
 * does not accept them yet).
 */
export function takeoverPayload(
  loaded: TakeoverFormFields,
  form: TakeoverFormFields,
): Pick<MyBridgeAgentInput, 'takeover' | 'operatorChatId' | 'autoResumeAfterMinutes'> {
  const out: Pick<MyBridgeAgentInput, 'takeover' | 'operatorChatId' | 'autoResumeAfterMinutes'> = {};
  const takeover: Partial<MyBridgeTakeoverDefaults> = {};
  if (form.takeoverEnabled !== loaded.takeoverEnabled) takeover.enabled = form.takeoverEnabled;
  if (form.takeoverByIncoming !== loaded.takeoverByIncoming) takeover.byIncomingMessage = form.takeoverByIncoming;
  if (Object.keys(takeover).length > 0) out.takeover = takeover;
  const op = form.operatorChatId.trim();
  if (op !== loaded.operatorChatId.trim()) out.operatorChatId = op || null;
  // #1059: null switches it off, a number sets it; an unchanged value is not sent. An INVALID on-state is never sent
  // (the form blocks save on it) - fail closed to "leave it alone".
  const loadedMin = loaded.autoResumeEnabled ? parseAutoResumeMinutes(loaded.autoResumeMinutes) : null;
  const formMin = form.autoResumeEnabled ? parseAutoResumeMinutes(form.autoResumeMinutes) : null;
  if (!autoResumeInvalid(form) && formMin !== loadedMin) out.autoResumeAfterMinutes = formMin;
  return out;
}
